import { and } from 'ember-truth-helpers';
import { didCancel } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import AwayMessageSection from '../away-message-section.gjs';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ConversationListItem from '../conversation-list-item.gjs';
import ConversationSearch from '../conversation-search.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import styles from './conversation-list.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesConversationList extends Component {
  @service('spds/toast') toast;
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get routeHandler() {
    return this.args.handler;
  }

  get conversations() {
    return this.routeHandler.conversations;
  }

  get currentProfile() {
    return this.routeHandler.currentProfile;
  }

  get groupMessagingEnabled() {
    return this.routeHandler.groupMessagingEnabled;
  }

  get hasSearchTerm() {
    return this.routeHandler.hasSearchTerm;
  }

  get isLoadingConversations() {
    return this.routeHandler.isLoadingConversations;
  }

  get sortedConversations() {
    return this.routeHandler.sortedConversations;
  }

  get toggleAwayStatus() {
    return this.routeHandler.toggleAwayStatus;
  }

  get search() {
    return this.messaging.search;
  }

  get headerText() {
    return this.intl.t('emc.navigation.conversation_list', {
      amount: this.currentProfile?.unreadConversationIds?.length || 0,
    });
  }

  loadMoreConversations = async model => {
    try {
      await this.routeHandler.loadMoreConversations.perform(model);
    } catch (err) {
      if (didCancel(err)) return;
      this.toast.error(this.intl.t('emc.general_error'));
    }
  };

  createConversation = () => {
    this.messagingNavigation.toContacts();
  };

  updateSearch = event => {
    const { value } = event.target;
    this.messaging.updateSearch(value);

    // SpdsInputSearch is not fully controlled by @value, so prevent its raw change
    // and replace it with the capped search value from the service.
    if (value !== this.search) {
      event.preventDefault();
      event.newValue = this.search;
      event.target.value = this.search;
    }
  };

  clearSearch = () => {
    this.messaging.clearSearch();
  };

  <template>
    <BaseRoute
      class="{{styles.component}} conversation-list-page"
      data-test-conversation-list-route
    >
      <:header>
        <div class="navigation-bar" data-test-navigation-bar>
          {{svgJar "messaging-locked" class="locked-icon"}}
          <h3 class="navigation-text" data-test-header>{{this.headerText}}</h3>
          {{#if this.currentProfile.isClinician}}
            <GhostButton
              @fitContent={{true}}
              @iconOnly={{true}}
              class="new-button"
              data-test-conversation-list-page-new-button
              {{on "click" this.createConversation}}
            >
              {{svgJar "messaging-compose"}}
            </GhostButton>
          {{/if}}
        </div>
      </:header>
      <:default>
        {{#if this.currentProfile.isClinician}}
          <AwayMessageSection @onToggleTask={{this.toggleAwayStatus}} />
        {{/if}}

        {{#if (and this.groupMessagingEnabled this.currentProfile.isClinician)}}
          <ConversationSearch
            @clearSearch={{this.clearSearch}}
            @search={{this.search}}
            @updateSearch={{this.updateSearch}}
          />
        {{/if}}

        {{#if this.isLoadingConversations}}
          <div class="spinner" data-test-loading-spinner>
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.sortedConversations.length}}
          <ul class="conversation-list-body" data-test-conversation-list-body>
            {{#each this.sortedConversations as |conversation|}}
              <ConversationListItem @conversation={{conversation}} />
            {{/each}}

            <SharedInfinityLoader
              @model={{this.conversations}}
              @infinityLoad={{this.loadMoreConversations}}
            />
          </ul>
        {{else if this.hasSearchTerm}}
          <div class="no-search-results" data-test-no-search-results>
            {{svgJar "messaging-no-search-results"}}
            <h3 class="empty-list-header" data-test-no-search-results-header>
              {{t "emc.conversation_list.no_search_results"}}
            </h3>
          </div>
        {{else}}
          <div class="empty-conversation-list" data-test-empty-conversation-list>
            {{svgJar "messaging-welcome"}}
            <h3 class="empty-list-header" data-test-empty-header>{{t "emc.empty_list_header"}}</h3>
            <p class="empty-list-text" data-test-empty-text>{{t "emc.empty_list_text"}}</p>
            {{#if this.currentProfile.isClinician}}
              <PrimaryButton
                class="create-message-button"
                data-test-empty-list-create-message-button
                {{on "click" this.createConversation}}
              >
                Create Message
              </PrimaryButton>
            {{/if}}
          </div>
        {{/if}}
      </:default>
    </BaseRoute>
  </template>
}
